import React, { useState, useEffect } from 'react';
import { formatearMoneda } from '../utils/moneda';
import { obtenerAnteojosPorEstado, actualizarEstadoAnteojo } from '../api/backend_v2';
import { formatearFechaArgentina } from '../utils/fechas';
import {
  ArchiveBoxIcon,
  ArrowUpTrayIcon,
  ArrowPathIcon,
  PhoneIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  BanknotesIcon,
  ChatBubbleLeftEllipsisIcon,
} from '@heroicons/react/24/outline';

const AnteojosParaRetirar = () => {
  const [anteojos, setAnteojos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actualizando, setActualizando] = useState(null);

  useEffect(() => {
    cargarAnteojos();
  }, []);

  const cargarAnteojos = async () => {
    try {
      setLoading(true);
      const response = await obtenerAnteojosPorEstado('Para retirar');
      setAnteojos(response.anteojos || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cambiarEstado = async (anteojoId, nuevoEstado) => {
    try {
      setActualizando(anteojoId);
      await actualizarEstadoAnteojo(anteojoId, nuevoEstado);
      await cargarAnteojos();
    } catch (err) {
      setError(err.message);
    } finally {
      setActualizando(null);
    }
  };

  // Usar función utilitaria
  const formatearFecha = formatearFechaArgentina;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header eliminado - se maneja en AnteojosSubNav */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            <strong>Error:</strong> {error}
            <button onClick={() => setError(null)} className="ml-4 text-red-500 hover:text-red-700">✕</button>
          </div>
        )}
        <div className="bg-white shadow rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-2xl font-bold text-pink-600">{anteojos.length}</div>
              <div className="text-sm text-gray-500">Anteojos Para Retirar</div>
            </div>
            <div className="flex items-center space-x-4">
              <button onClick={cargarAnteojos} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"><ArrowPathIcon className="h-4 w-4 mr-1" />Actualizar</button>
            </div>
          </div>
        </div>
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          {anteojos.length === 0 ? (
            <div className="text-center py-12">
              <ArchiveBoxIcon className="mx-auto h-10 w-10 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">¡Sin anteojos para retirar!</h3>
              <p className="mt-1 text-sm text-gray-500">Todos los anteojos ya fueron entregados o están en otra etapa</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {anteojos.map((anteojo) => (
                <li key={anteojo.id}>
                  <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 rounded-full bg-pink-100 flex items-center justify-center">
                            <ArchiveBoxIcon className="h-5 w-5 text-pink-600" />
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-medium text-gray-900">{anteojo.cliente.nombre} {anteojo.cliente.apellido}</span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-800">{anteojo.tipo_anteojo}</span>
                          </div>
                          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <PhoneIcon className="h-3.5 w-3.5 flex-shrink-0" />
                              {anteojo.cliente.telefono}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <CalendarIcon className="h-3.5 w-3.5 flex-shrink-0" />
                              Pedido: {formatearFecha(anteojo.fecha_pedido)}
                            </div>
                            {anteojo.nro_sobre && (
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <ClipboardDocumentListIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                Sobre: #{anteojo.nro_sobre}
                              </div>
                            )}
                          </div>
                          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <CurrencyDollarIcon className="h-3.5 w-3.5 flex-shrink-0" />
                              Total: {formatearMoneda(anteojo.precio_total)}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <BanknotesIcon className="h-3.5 w-3.5 flex-shrink-0" />
                              Seña: {formatearMoneda(anteojo.sena)}
                            </div>
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <BanknotesIcon className="h-3.5 w-3.5 flex-shrink-0" />
                              Saldo: {formatearMoneda(anteojo.saldo)}
                            </div>
                          </div>
                          {anteojo.observaciones && (
                            <div className="mt-1.5 flex items-center gap-1 text-xs text-gray-500 italic">
                              <ChatBubbleLeftEllipsisIcon className="h-3.5 w-3.5 flex-shrink-0" />
                              {anteojo.observaciones}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto sm:flex-shrink-0">
                        <button
                          onClick={() => cambiarEstado(anteojo.id, 'Entregado')}
                          disabled={actualizando === anteojo.id}
                          className="flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-3 py-2 border border-transparent text-xs font-medium rounded text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                        >
                          {actualizando === anteojo.id ? (
                            <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <ArrowUpTrayIcon className="h-3.5 w-3.5" />
                          )}
                          Marcar Entregado
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnteojosParaRetirar;
